import { useState } from "react";
import api, { apiError } from "../../lib/api";
import { toast } from "sonner";
import { AudiencePicker } from "./AudiencePicker";

const VIS = [["owner", "Visible only to you"], ["admins", "Visible to you and all admins"]];
const EMPTY = { name: "", visibility: "owner", member_user_ids: [] };

export const CircleManager = ({ circles, members, onInvite, reload }) => {
  const [f, setF] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const save = async () => {
    if (!f.name.trim()) { toast.error("Give the group a name."); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/circles/${editing}`, f);
      else await api.post("/admin/circles", f);
      toast.success(editing ? "Group updated." : "Private group created.");
      setF(EMPTY); setEditing(null); reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const edit = (c) => {
    setEditing(c.circle_id);
    setF({ name: c.name, visibility: c.visibility, member_user_ids: c.member_user_ids || [] });
    setOpen(true);
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/circles/${c.circle_id}`); toast.success("Group deleted."); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border border-[#C69A4E]/40 p-4 mb-10" data-testid="circle-manager">
      <button className="flex items-center justify-between w-full" onClick={() => setOpen(!open)} data-testid="circle-manager-toggle">
        <span className="t-label !text-[#C69A4E]">Private groups ({circles.length})</span>
        <span className="t-cap text-muted-s">{open ? "Hide" : "Manage"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-5">
          {circles.length > 0 && (
            <table className="table-solera" data-testid="circle-list">
              <thead><tr><th>Group</th><th>Visibility</th><th className="num">Members</th><th className="num">Deals</th><th></th></tr></thead>
              <tbody>
                {circles.map((c) => (
                  <tr key={c.circle_id} data-testid={`circle-row-${c.circle_id}`}>
                    <td className="text-bone">{c.name}</td>
                    <td className="text-muted-s t-small">{c.visibility === "admins" ? "You + all admins" : "Only you"}</td>
                    <td className="num text-muted-s">{c.members?.length || 0}</td>
                    <td className="num text-muted-s">{c.deal_count || 0}</td>
                    <td className="text-right whitespace-nowrap space-x-4">
                      <button className="t-label hover:!text-[#E8E6E1]" onClick={() => edit(c)} data-testid={`circle-edit-${c.circle_id}`}>Edit</button>
                      <button className="t-label hover:!text-[#E8E6E1]" onClick={() => del(c)} data-testid={`circle-delete-${c.circle_id}`}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="space-y-3 border-t border-[#24272B] pt-4">
            <span className="t-label">{editing ? "Edit group" : "New private group"}</span>
            <div className="flex flex-wrap gap-3">
              <input className="input-solera max-w-xs" placeholder="Group name, e.g. Founders Circle"
                value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="circle-name-input" />
              <select className="input-solera max-w-xs" value={f.visibility}
                onChange={(e) => setF({ ...f, visibility: e.target.value })} data-testid="circle-visibility-select">
                {VIS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <AudiencePicker members={members} value={f.member_user_ids}
              onChange={(v) => setF({ ...f, member_user_ids: v })} onInvite={onInvite} testidPrefix="circle" />
            <div className="flex gap-3">
              {editing && <button className="btn-ghost" onClick={() => { setEditing(null); setF(EMPTY); }} data-testid="circle-cancel">Cancel</button>}
              <button className="btn-primary" disabled={busy || !f.name.trim()} onClick={save} data-testid="circle-save">
                {editing ? "Save group" : "Create group"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
